import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useEffect, useMemo, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { leadsApi } from '@/api/leads';
import { IncludeNonAudienceToggle } from '@/components/common/IncludeNonAudienceToggle';
import { SignalBars } from '@/components/common/SignalBars';
import { LeadSidePanel } from '@/components/common/LeadSidePanel';
import { PersonAvatar, CompanyAvatar } from '@/components/common/Avatar';
import { PhoneEnrichmentCell } from '@/components/common/PhoneEnrichmentCell';
import { EnrichmentStatusCell } from '@/components/common/EnrichmentStatusCell';
import { getWebTypeMeta } from '@/constants/webTypes';
import { timeFromNowVerbose } from '@/utils/time';
import { RefreshCcw } from 'lucide-preact';
import { getLeadComparator, getCompanyComparator } from '@/utils/leadSorting';
import { getLeadDisplayName, shouldShowEmailLine } from '@/utils/leadFormatting';
import { LoadingRow, ErrorState, EmptyState } from '@/components/common/TableFragments';
import { DataGrid } from '@/components/common/grids';
import { useCompany } from '@/hooks/useCompany';
import { useUserProfile } from '@/hooks/useUserProfile';
import { LEAD_EXPORT_COLUMNS, COMPANY_EXPORT_COLUMNS, generateExportFilename } from '@/utils/leadExport';
// ---------------- Mode Hook (path-based) ----------------
function useWebLeadsMode(baseWebPath) {
    const location = useLocation();
    // Derive from path each render
    const path = location?.path || location?.url || (typeof window !== 'undefined' ? window.location.pathname : '');
    const mode = /\/leads\/companies$/.test(path) ? 'companies' : 'contacts';
    const setMode = (next) => {
        if (next === mode)
            return;
        const target = next === 'contacts' ? `${baseWebPath}/leads` : `${baseWebPath}/leads/companies`;
        try {
            location.route(target);
        }
        catch {
            if (typeof window !== 'undefined') {
                window.history.pushState({}, '', target);
                window.dispatchEvent(new PopStateEvent('popstate'));
            }
        }
    };
    return [mode, setMode];
}
// ---------------- Main Component ----------------
export const WebLeadsPanel = ({ clientCompanyId, workspaceId, campaignId, webId, baseWebPath, web }) => {
    const { company, currentWorkspace } = useCompany();
    const { getWorkspaceCapabilities } = useUserProfile();
    const { canEdit } = getWorkspaceCapabilities(clientCompanyId, workspaceId);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mode, setMode] = useWebLeadsMode(baseWebPath);
    // When false: show only audience-targeted leads (IsAudienceTarget true or undefined) . When true: include non-audience leads.
    const [includeNonAudience, setIncludeNonAudience] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const EMPTY = { CampaignId: campaignId, Leads: [], Companies: [], LeadCount: 0, CompanyCount: 0, GeneratedAtUtc: new Date().toISOString() };
    const load = (signal) => {
        setLoading(true);
        setError(null);
        leadsApi.getWebLeads(campaignId, workspaceId, webId, signal)
            .then(r => {
            const payload = r?.data;
            if (payload && Array.isArray(payload.Leads))
                setData(payload);
            else
                setData(EMPTY);
        })
            .catch(e => {
            // Don't show error if request was aborted
            if (e instanceof Error && e.name === 'AbortError')
                return;
            setError(e?.message || 'Unknown error');
            setData(EMPTY);
        })
            .finally(() => setLoading(false));
    };
    useEffect(() => {
        const abortController = new AbortController();
        load(abortController.signal);
        return () => abortController.abort();
    }, [campaignId, workspaceId, webId]);
    const generatedRelative = data ? timeFromNowVerbose(data.GeneratedAtUtc) : '—';
    const leads = data?.Leads || [];
    const companies = data?.Companies || [];
    // Check if there's an Online Event web type in any lead
    const hasOnlineEvent = leads.some(l => l.WebTypes?.includes('Online Event'));
    // Filtering semantics:
    //  - includeNonAudience = false => only audience targets (treat missing IsAudienceTarget as true for backward compatibility)
    //  - includeNonAudience = true  => all leads (no filtering)
    const filteredLeads = mode === 'contacts'
        ? (includeNonAudience ? leads : leads.filter(l => l.IsAudienceTarget !== false))
        : leads;
    // Custom comparator for attributionSource that uses the specific webId
    const getWebLeadComparator = (key) => {
        if (key === 'attributionSource') {
            return (a, b) => {
                const aSource = a.WebSourceAttributions?.find(attr => attr.WebId === webId)?.Source || '';
                const bSource = b.WebSourceAttributions?.find(attr => attr.WebId === webId)?.Source || '';
                return aSource.localeCompare(bSource);
            };
        }
        return getLeadComparator(key);
    };
    // Deselect lead if filtered out
    useEffect(() => { if (selectedLead && !filteredLeads.some(l => l.LeadId === selectedLead.LeadId))
        setSelectedLead(null); }, [filteredLeads, selectedLead]);
    // Close on ESC
    useEffect(() => { if (!selectedLead)
        return; const h = (e) => { if (e.key === 'Escape')
        setSelectedLead(null); }; window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h); }, [selectedLead]);
    // Helper to update a lead in the data state
    const updateLeadInState = (leadId, updates) => {
        setData(prev => {
            if (!prev)
                return prev;
            const nextLeads = prev.Leads.map(lead => {
                if (lead.LeadId !== leadId)
                    return lead;
                return { ...lead, ...updates };
            });
            return { ...prev, Leads: nextLeads };
        });
        setSelectedLead(curr => {
            if (!curr || curr.LeadId !== leadId)
                return curr;
            return { ...curr, ...updates };
        });
    };
    // ---------- Contact columns for DataGrid ----------
    const contactColumns = useMemo(() => {
        const cols = [
            {
                key: 'lead',
                header: 'Lead',
                sortable: true,
                required: true,
                className: 'whitespace-nowrap',
                render: (l) => {
                    const name = getLeadDisplayName(l);
                    const showEmail = shouldShowEmailLine(l, name);
                    return (_jsxs("div", { class: "flex items-center gap-3", children: [_jsx(PersonAvatar, { firstName: l.FirstName, lastName: l.LastName, fullName: l.FullName, profilePicUrl: l.ProfilePicUrl, alt: name }), _jsxs("div", { class: "flex flex-col leading-tight min-w-0", children: [_jsx("span", { class: "font-medium text-gray-900 dark:text-gray-100 truncate max-w-[180px]", title: name, children: name }), showEmail && l.Email && _jsx("span", { class: "text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]", title: l.Email, children: l.Email })] })] }));
                },
            },
            {
                key: 'phone',
                header: 'Phone',
                sortable: true,
                render: (l, index) => (_jsx(PhoneEnrichmentCell, { phone: l.Phone, globalContactPhone: l.GlobalContactPhone, phoneEnrichmentAvailableDate: l.PhoneEnrichmentAvailableDate, id: l.LeadId, clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId, canEdit: canEdit, isLastRow: index === filteredLeads.length - 1 && filteredLeads.length > 1, onPhoneUpdated: (updated) => {
                        const phoneUpdate = { ...updated, Phone: updated.GlobalContactPhone || updated.Phone };
                        console.log('[WebLeadsPanel] onPhoneUpdated received:', { updated, phoneUpdate, leadId: l.LeadId });
                        updateLeadInState(l.LeadId, phoneUpdate);
                    } })),
            },
            {
                key: 'enrichment',
                header: 'Enrichment',
                sortable: true,
                render: (l) => (_jsx(EnrichmentStatusCell, { enrichmentStatus: l.EnrichmentStatus, enrichedAt: l.EnrichedAt, enrichmentAvailableDate: l.EnrichmentAvailableDate, enrichmentAttemptCount: l.EnrichmentAttemptCount, clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId, id: l.LeadId, onEnrichmentUpdated: (updated) => updateLeadInState(l.LeadId, updated) })),
            },
            {
                key: 'company',
                header: 'Company',
                sortable: true,
                className: 'truncate max-w-[160px]',
                render: (l) => _jsx("span", { title: l.Company || '—', children: l.Company || '—' }),
            },
            {
                key: 'title',
                header: 'Title',
                sortable: true,
                className: 'truncate max-w-[180px]',
                render: (l) => _jsx("span", { title: l.JobTitle || '—', children: l.JobTitle || '—' }),
            },
            {
                key: 'types',
                header: 'Types',
                render: (l) => (_jsx("div", { class: "flex flex-wrap gap-1", children: l.WebTypes?.map(wt => {
                        const meta = getWebTypeMeta(wt);
                        return _jsx("span", { class: `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${meta?.chipClass || 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`, children: wt }, wt);
                    }) })),
            },
            {
                key: 'attributionSource',
                header: 'Attribution Source',
                sortable: true,
                className: 'whitespace-nowrap',
                render: (l) => _jsx(_Fragment, { children: l.WebSourceAttributions?.find(attr => attr.WebId === webId)?.Source || '—' }),
            },
        ];
        // Dynamic column: webinar attended (only when an Online Event web type exists)
        if (hasOnlineEvent) {
            cols.push({
                key: 'webinarAttended',
                header: 'Webinar Attended',
                sortable: true,
                defaultVisible: false,
                className: 'whitespace-nowrap',
                render: (l) => _jsx(_Fragment, { children: l.WebinarAttended === true ? 'Yes' : l.WebinarAttended === false ? 'No' : '—' }),
            });
        }
        cols.push({
            key: 'lastConversion',
            header: 'Last Conversion',
            sortable: true,
            className: 'whitespace-nowrap',
            render: (l) => _jsx(_Fragment, { children: l.LastInteractionAt ? timeFromNowVerbose(l.LastInteractionAt) : '—' }),
        }, {
            key: 'signal',
            header: 'Campaign Signal',
            sortable: true,
            required: true,
            className: 'whitespace-nowrap',
            render: (l) => _jsx(SignalBars, { score: l.SignalScore }),
        });
        return cols;
    }, [clientCompanyId, workspaceId, campaignId, webId, hasOnlineEvent, filteredLeads.length]);
    // ---------- Company columns for DataGrid ----------
    const companyColumns = useMemo(() => [
        {
            key: 'company',
            header: 'Company',
            sortable: true,
            required: true,
            className: 'whitespace-nowrap',
            render: (c) => (_jsxs("div", { class: "flex items-center gap-3", children: [_jsx(CompanyAvatar, { companyName: c.CompanyName, logoUrl: c.CompanyLogoUrl }), _jsx("div", { children: c.CompanyLinkedInUrl
                            ? _jsx("a", { href: c.CompanyLinkedInUrl, target: "_blank", rel: "noopener noreferrer", class: "font-medium text-gray-900 dark:text-gray-100 hover:underline", title: c.CompanyLinkedInUrl, children: c.CompanyName })
                            : c.CompanyWebsite
                                ? _jsx("a", { href: c.CompanyWebsite, target: "_blank", rel: "noopener noreferrer", class: "font-medium text-gray-900 dark:text-gray-100 hover:underline", title: c.CompanyWebsite, children: c.CompanyName })
                                : _jsx("span", { class: "font-medium text-gray-900 dark:text-gray-100", children: c.CompanyName }) })] })),
        },
        {
            key: 'url',
            header: 'URL',
            className: 'truncate max-w-[180px]',
            render: (c) => {
                if (!c.CompanyWebsite)
                    return _jsx(_Fragment, { children: '—' });
                try {
                    const host = new URL(c.CompanyWebsite).hostname.replace(/^www\./, '');
                    return _jsx("a", { href: c.CompanyWebsite, target: "_blank", rel: "noopener noreferrer", class: "hover:underline", title: c.CompanyWebsite, children: host });
                }
                catch {
                    return _jsx("a", { href: c.CompanyWebsite, target: "_blank", rel: "noopener noreferrer", class: "hover:underline", title: c.CompanyWebsite, children: c.CompanyWebsite });
                }
            },
        },
        {
            key: 'industry',
            header: 'Industry',
            sortable: true,
            className: 'truncate max-w-[160px]',
            render: (c) => _jsx("span", { title: c.Industry || '—', children: c.Industry || '—' }),
        },
        {
            key: 'size',
            header: 'Size',
            sortable: true,
            render: (c) => _jsx(_Fragment, { children: c.StaffCountRange || '—' }),
        },
        {
            key: 'lastConversion',
            header: 'Last Conversion',
            sortable: true,
            className: 'whitespace-nowrap',
            render: (c) => _jsx(_Fragment, { children: c.LastConversionAt ? timeFromNowVerbose(c.LastConversionAt) : '—' }),
        },
        {
            key: 'leads',
            header: 'Leads',
            sortable: true,
            render: (c) => _jsx(_Fragment, { children: c.LeadCount }),
        },
        {
            key: 'signal',
            header: 'Campaign Signal',
            sortable: true,
            required: true,
            className: 'whitespace-nowrap',
            render: (c) => _jsx(SignalBars, { score: c.SignalScore }),
        },
    ], []);
    // ---------- Export configs for DataGrid ----------
    const contactExportConfig = useMemo(() => ({
        getFilename: () => generateExportFilename(company?.Name, currentWorkspace?.Name, 'Web', 'leads'),
        columns: LEAD_EXPORT_COLUMNS.map(col => ({ header: col.label, getValue: col.getValue })),
    }), [company?.Name, currentWorkspace?.Name]);
    const companyExportConfig = useMemo(() => ({
        getFilename: () => generateExportFilename(company?.Name, currentWorkspace?.Name, 'Web', 'companies'),
        columns: COMPANY_EXPORT_COLUMNS.map(col => ({ header: col.label, getValue: col.getValue })),
    }), [company?.Name, currentWorkspace?.Name]);
    const MetricsBar = () => (_jsxs("div", { class: "flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between mb-3", children: [_jsxs("div", { class: "flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-600 dark:text-gray-300", children: [_jsxs("span", { children: [_jsx("span", { class: "font-semibold text-gray-900 dark:text-gray-100", children: "Conversions:" }), " ", data?.LeadCount ?? '—'] }), _jsxs("span", { children: [_jsx("span", { class: "font-semibold text-gray-900 dark:text-gray-100", children: "Companies:" }), " ", data?.CompanyCount ?? '—'] }), _jsxs("span", { class: "hidden xs:inline", children: [_jsx("span", { class: "font-semibold text-gray-900 dark:text-gray-100", children: "Generated:" }), " ", generatedRelative] }), mode === 'contacts' && (_jsx(IncludeNonAudienceToggle, { value: includeNonAudience, onToggle: () => setIncludeNonAudience(v => !v) }))] }), _jsxs("div", { class: "flex items-center gap-2", children: [_jsx(LeadsModeSwitch, { mode: mode, onChange: m => setMode(m) }), _jsxs("button", { onClick: () => load(), class: "inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700", title: "Refresh", children: [_jsx(RefreshCcw, { class: "w-4 h-4" }), "Refresh"] })] })] }));
    if (loading)
        return (_jsxs("div", { class: "w-full", children: [_jsx(MetricsBar, {}), _jsx("table", { class: "min-w-full text-sm", children: _jsx("tbody", { children: Array.from({ length: 3 }).map((_, i) => _jsx(LoadingRow, {}, i)) }) })] }));
    if (error)
        return (_jsxs("div", { children: [_jsx(MetricsBar, {}), _jsx(ErrorState, { error: error, onRetry: () => load() })] }));
    const showLeads = mode === 'contacts';
    const showCompanies = mode === 'companies';
    if (showLeads && filteredLeads.length === 0)
        return (_jsxs("div", { children: [_jsx(MetricsBar, {}), _jsx(EmptyState, { message: includeNonAudience ? 'No leads yet.' : 'No audience leads yet.' })] }));
    if (showCompanies && companies.length === 0)
        return (_jsxs("div", { children: [_jsx(MetricsBar, {}), _jsx(EmptyState, { message: "No company signal yet." })] }));
    return (_jsxs(_Fragment, { children: [_jsxs("div", { class: "w-full", children: [_jsx(MetricsBar, {}), showLeads && (_jsx(DataGrid, { data: filteredLeads, columns: contactColumns, getRowKey: (l) => l.LeadId, onRowClick: setSelectedLead, getSortComparator: getWebLeadComparator, scrollable: true, columnVisibilityKey: "orbitalx-lead-columns-web", exportConfig: contactExportConfig })), showCompanies && (_jsx(DataGrid, { data: companies, columns: companyColumns, getRowKey: (c) => c.CompanyId || c.CompanyName, getSortComparator: getCompanyComparator, scrollable: true, columnVisibilityKey: "orbitalx-company-columns-web", exportConfig: companyExportConfig }))] }), selectedLead && (_jsx(LeadSidePanel, { lead: selectedLead, onClose: () => setSelectedLead(null), touchpoints: selectedLead.CampaignTouchpoints, clientCompanyId: clientCompanyId, workspaceId: workspaceId, campaignId: campaignId, webId: webId, web: web, onRefreshNeeded: () => load(), onLeadDeleted: (leadId) => {
                    setData(prev => {
                        if (!prev)
                            return prev;
                        return {
                            ...prev,
                            Leads: prev.Leads.filter(l => l.LeadId !== leadId),
                            LeadCount: prev.LeadCount - 1
                        };
                    });
                    setSelectedLead(null);
                }, onLeadUpdated: (updated) => {
                    setData(prev => {
                        if (!prev)
                            return prev;
                        const nextLeads = prev.Leads.map(l => {
                            if (l.LeadId !== updated.LeadId)
                                return l;
                            const merged = { ...l, ...updated };
                            if (updated.FirstName !== undefined || updated.LastName !== undefined) {
                                const fn = [merged.FirstName, merged.LastName].filter(Boolean).join(' ');
                                merged.FullName = fn.length > 0 ? fn : merged.FullName;
                            }
                            return merged;
                        });
                        return { ...prev, Leads: nextLeads };
                    });
                    setSelectedLead(curr => {
                        if (!curr || curr.LeadId !== updated.LeadId)
                            return curr;
                        const merged = { ...curr, ...updated };
                        if (updated.FirstName !== undefined || updated.LastName !== undefined) {
                            const fn = [merged.FirstName, merged.LastName].filter(Boolean).join(' ');
                            merged.FullName = fn.length > 0 ? fn : merged.FullName;
                        }
                        return merged;
                    });
                } }))] }));
};
// ---------------- Mode Switch ----------------
const LeadsModeSwitch = ({ mode, onChange }) => {
    const options = [
        { key: 'contacts', label: 'Contacts' },
        { key: 'companies', label: 'Companies' }
    ];
    return (_jsx("div", { role: "radiogroup", "aria-label": "Leads view mode", class: "inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden", children: options.map(o => {
            const active = o.key === mode;
            return (_jsx("button", { role: "radio", "aria-checked": active, type: "button", onClick: () => { if (!active)
                    onChange(o.key); }, class: `px-3 py-1.5 text-xs font-medium transition focus:outline-none focus:z-10 ${active ? 'bg-green-600 text-white dark:bg-green-500' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`, children: o.label }, o.key));
        }) }));
};
export default WebLeadsPanel;
